import { Immutable } from "immer";
import React from "react";

import type { PromoProductBlock as Value } from "@reactivated";

// NOTE: This component reproduces the legacy server-rendered markup from
// `cms/home_page.html` (the `block_type == 'promo_product_block'` branch)
// verbatim — every classname is load-bearing for the existing SCSS, so keep
// them in sync. In particular `al-hp-promo-module__button` is an analytics
// classname that MUST survive on the CTA `<a>`.
//
// Field handling: only `title` is a RichTextBlock (HTML) — render it via
// dangerouslySetInnerHTML on the existing `__title` div (the legacy
// `{{ block.value.title }}` emitted the RichText HTML directly). `pre_title`,
// `description`, and `link_name` are plain CharBlocks → render as escaped text
// children to match Django autoescape.
type IProps = Immutable<{
    value: Value;
}>;

const backgroundImageStyle = (
    image: Immutable<Value["image"]>,
): React.CSSProperties | undefined => {
    // Legacy emitted `style="background-image: url(<url>);"`. When the image is
    // null the legacy markup rendered `url()` (a broken url); we instead omit
    // the inline style entirely while keeping the wrapper element. See the
    // LifestyleModule / ComparisonCarouselBlock precedent.
    if (!image) {
        return undefined;
    }
    return { backgroundImage: `url(${image.url})` };
};

export const PromoProductBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;

    return (
        <div
            className="l-capped-width home-page-promo-module"
            style={backgroundImageStyle(v.image)}
        >
            <div className="home-page-promo-module__copy home-page-promo-module--height">
                <div className="home-page-promo-module__copy-inner">
                    <div className="home-page-promo-module__pre-title">
                        {v.pre_title ?? ""}
                    </div>
                    <div
                        className="home-page-promo-module__title home-page-section__title"
                        dangerouslySetInnerHTML={{ __html: v.title ?? "" }}
                    />
                    <div className="home-page-promo-module__desc home-page-section__desc">
                        {v.description ?? ""}
                    </div>
                    <a
                        className="button button--ming home-page-promo-module__button al-hp-promo-module__button"
                        href={v.link?.url ?? ""}
                    >
                        <span>{v.link_name ?? ""}</span>
                    </a>
                </div>
            </div>
        </div>
    );
};
